
import Vue from 'vue';

/** Renders pagination controls, inputs are 0-based, meaning page === 0 will render as 1 on the label */
export default Vue.extend({
	props: {
		page: Number,
		page2: {
			type: Number,
			required: false,
		},
		pageActive: {
			type: Boolean,
			default: true
		},
		maxPage: {
			type: Number,
			default: Number.MAX_VALUE,
		},
		minPage: {
			type: Number,
			default: 0,
		},
		disabled: Boolean,
		editable: {
			type: Boolean,
			default: true
		},
		showOffsets: {
			type: Boolean,
			default: true
		},
		/** Show e.g. 1/10 instead of just '1' in the centre button. Only has an effect when editable is false. */
		showTotal: {
			type: Boolean,
			default: false,
		},
	},
	data: () => ({
		focus: false,
	}),
	computed: {
		/** Whether we're showing a range of pages (from a shared URL with different page size) */
		hasPageRange(): boolean { return this.page2 != null && this.page2 !== this.page; },
		currentPageLabel(): string {
			const baseRangeString = this.hasPageRange
				? `${(this.boundedLowerPage+1).toLocaleString()} - ${(this.boundedUpperPage+1).toLocaleString()}`
				: (this.boundedLowerPage+1).toLocaleString();
			return this.showTotal ? `${baseRangeString}/${(this.maxPage+1).toLocaleString()}` : baseRangeString;
		},

		lowerPages(): number[] {
			return this.calcOffsets(this.boundedLowerPage - this.minPage).reverse().map(o => this.boundedLowerPage - o);
		},
		higherPages(): number[] {
			return this.calcOffsets(this.maxPage - this.boundedUpperPage).map(o => this.boundedUpperPage + o);
		},
		nextEnabled(): boolean {
			return this.boundedUpperPage < this.maxPage;
		},
		prevEnabled(): boolean {
			return this.boundedLowerPage > this.minPage;
		},

		boundedLowerPage(): number { return Math.max(this.minPage, Math.min(this.page, this.maxPage)); },
		boundedUpperPage(): number { return Math.max(this.minPage, Math.min(this.page2 ?? this.page, this.maxPage)); }
	},
	methods: {
		calcOffsets(range: number) {
			if (range <= 0) return [];
			if (range <= 1) return [1];
			if (range <= 2) return [1,2];
			if (range <= 5) return [1,2,range];
			if (range <= 10) return [1,2,5,range];
			return [1,2,3,5,10];
		},
		isValid(page: any): page is number {
			return typeof page === 'number' &&
				!isNaN(page) &&
				(page !== this.page || !this.pageActive) && // emit event for current page if the page is not active (i.e. is page is just center of the pagination, but not the "current" page)
				page >= this.minPage &&
				page <= this.maxPage
		},
		changePage(page: any) {
			if (!this.disabled && this.isValid(page)) {
				this.$emit('change', page)
			}
		}
	},
	beforeUpdate() {
		this.focus = document.activeElement === this.$refs.maincontrol;
	},
	updated() {
		if (this.focus) {
			(this.$refs.maincontrol as HTMLInputElement).focus();
		}
	},

})
